import { PostgrestError } from "@supabase/supabase-js";
import { Notification } from "providers/NotifierProvider";
import { supabase } from "supabase/client";
import { Database } from "supabase/types";
import logSentryError from "utils/logSentryError";

type DbSecretSanta = Database["public"]["Tables"]["secret_santa"];
export type SecretSantaRow = DbSecretSanta["Row"];
export type SecretSantaInsert = DbSecretSanta["Insert"];
export type SecretSantaUpdate = DbSecretSanta["Update"];

export const SECRET_SANTA_ACTIONS = {
  CANCEL: "CANCEL",
  START: "START",
  REQUEST_WISHLIST: "REQUEST_WISHLIST",
  UPDATE_WISHLIST: "UPDATE_WISHLIST",
} as const;

export type SecretSantaAction = keyof typeof SECRET_SANTA_ACTIONS;

class SecretSantaApi {
  showNotification?: (notification: Notification) => void;

  constructor(showNotification?: (notification: Notification) => void) {
    this.showNotification = showNotification;
  }

  async manageSecretSanta(
    eventId: number,
    groupId: number,
    action: SecretSantaAction
  ): Promise<{
    error: PostgrestError | null;
  }> {
    const { error } = await supabase.functions.invoke("secret-santa", {
      body: JSON.stringify({ eventId, groupId, action }),
    });

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message:
            action === "START" ? "Unable to generate Secret Santa pairs." : "Unable to cancel Secret Santa pairs.",
          severity: "error",
        });
      }

      logSentryError(error, "SECRET_SANTA_API: manageSecretSanta");
      return { error };
    }

    return { error: null };
  }

  async getMyWishListForSanta(
    eventId: number,
    receiverId: number
  ): Promise<{
    data: SecretSantaRow["wishlist"] | null;
    error: PostgrestError | null;
  }> {
    const { data, error } = await supabase.rpc("get_secret_santa_wish_list", {
      event_id_param: eventId,
      receiver_id_param: receiverId,
    });

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to load wishlist.",
          severity: "error",
        });
      }

      logSentryError(error, "SECRET_SANTA_API: getMyWishListForSanta");
      return { data: null, error };
    }

    return { data: data as SecretSantaRow["wishlist"], error: null };
  }

  async getMyPairWishlist(
    userId: number,
    eventId: number
  ): Promise<{
    data: GetMyPairWishlistRes | null;
    error: PostgrestError | null;
  }> {
    const { data, error } = await supabase
      .from("secret_santa")
      .select("receiver_id, wishlist, requested_at")
      .eq("event_id", eventId)
      .eq("giver_id", userId)
      .single();

    if (error && error.code !== "PGRST116") {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to load pair wishlist.",
          severity: "error",
        });
      }

      logSentryError(error, "SECRET_SANTA_API: getMyPairWishlist");
      return { data, error };
    }

    return { data, error: null };
  }

  async updateWishList(
    eventId: number,
    groupId: number,
    wishlist: string[],
    action: SecretSantaAction
  ): Promise<{
    error: PostgrestError | null;
  }> {
    const { error } = await supabase.functions.invoke("secret-santa", {
      body: JSON.stringify({
        eventId,
        groupId,
        wishlist,
        action,
      }),
    });

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to update wishlist.",
          severity: "error",
        });
      }

      logSentryError(error, "SECRET_SANTA_API: updateWishList");
      return { error };
    }

    return { error: null };
  }

  async requestWishlist(
    eventId: RequestWishlistParams["eventId"],
    groupId: RequestWishlistParams["groupId"],
    action: RequestWishlistParams["action"]
  ): Promise<{
    error: PostgrestError | null;
  }> {
    const { error } = await supabase.functions.invoke("secret-santa", {
      body: JSON.stringify({
        eventId,
        groupId,
        action,
      }),
    });

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to request wishlist.",
          severity: "error",
        });
      }

      logSentryError(error, "SECRET_SANTA_API: requestWishlist");
      return { error };
    }

    return { error: null };
  }
}

export default SecretSantaApi;

export type GetMyPairWishlistRes = Pick<SecretSantaRow, "receiver_id" | "wishlist" | "requested_at">;

export interface RequestWishlistParams {
  eventId: number;
  groupId: number;
  action: SecretSantaAction;
}
